import { useHistory } from 'react-router';
import { bindActionCreators, Dispatch } from 'redux';
import { connect, RootStateOrAny } from 'react-redux';

import { Input, Card, Button, Form, Table } from 'antd';

import notifier from 'utils/notifier';
import { ErrorResponse } from 'utils/types';

import { checkCurrentPassword, checkPasswordHistory, updatePassword } from 'store/auth/actions';

import FloatLabel from 'components/Common/FloatLabel';

import { ChangePasswordProps } from './Proptypes';
import './index.scss';

const ChangePasswordComponent = (props: ChangePasswordProps) => {

  const history = useHistory();
  const [form] = Form.useForm();
  const UPDATE_SUCCESS = 1;

  const checkCurrentPassword = (debouncedCurrentPassword: string) => {
    return props.checkCurrentPassword(debouncedCurrentPassword, props.uid)
      .then((response: boolean) => {
        return response;
      })
      .catch((err: ErrorResponse) => {
        notifier.error(err.response.data.Message || 'Something went wrong, please contact a system administrator.');
        return false;
      });
  }

  const checkPasswordHistory = (newPassword: string) => {
    return props.checkPasswordHistory(props.uid, newPassword)
      .then((response: boolean) => {
        return response;
      })
      .catch((err: ErrorResponse) => {
        notifier.error(err.response.data.Message || 'Something went wrong, please contact a system administrator.');
        return false;
      });
  }

  const submit = () => {
    form.validateFields()
      .then(values => {
        props.updatePassword(values.newPassword, props.uid)
          .then((response: number) => {
            if (response === UPDATE_SUCCESS) {
              notifier.success('Password Change Success!');
              props.setVisible?.();
            } else {
              notifier.error('Something went wrong.');
            }
            return response;
          })
          .catch((err: ErrorResponse) => {
            notifier.error('Something went wrong, please contact a system administrator.');
          });
      })
      .catch(err => {
        notifier.error('Check your password entries.');
      });
  };

  return (
    <>
    <div className="profile-page">
      <Card className="profile-form">
        <Form
          form={form}
          name="changePasswordForm"
          onFinish={submit}
        >
            <FloatLabel 
              label="Current Password" 
              name="currentPassword"
              form={form}
              validateTrigger={['onChange', 'onBlur']}
              required
              rules={[
                () => ({
                  async validator(_: any, value: string) {
                    if (!value || await checkCurrentPassword(value)) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('We could not find this password. Please enter your current password.'));
                  },
                }),
              ]}
            >
              <Input
                type="password"
                autoComplete="current-password"
              />
            </FloatLabel>

          <FloatLabel 
            label="New Password" 
            name="newPassword"
            form={form}
            validateTrigger={['onChange', 'onBlur']}
            dependencies={['currentPassword']}
            required
            rules={[
              ({ getFieldValue } : any) => ({
                async validator(_: any, value: string) {
                  let checkCurrent = value && await checkCurrentPassword(value);
                  let checkHistory = value && await checkPasswordHistory(value);
                  if (!value || 
                      (value.match(/^((?=.*?[A-Z])(?=.*?[a-z])(?=.*[^A-Za-z0-9\s])|(?=.*?[0-9])(?=.*?[a-z])(?=.*[^A-Za-z0-9\s])|(?=.*?[0-9])(?=.*?[A-Z])(?=.*[^A-Za-z0-9\s])|(?=.*?[0-9])(?=.*?[A-Z])(?=.*?[a-z]))(?=.{6,})/) &&
                      value !== getFieldValue('currentPassword') && 
                      !value.includes(props.uid) &&
                      !checkCurrent &&
                      !checkHistory
                    )) {
                      return Promise.resolve();
                    } else if (checkHistory || checkCurrent) {
                      return Promise.reject(new Error('Please enter a different password from your previous ones.'));
                    }
                    return Promise.reject(new Error('Does not meet the password requirements below.'));
                },
              }),
            ]}
          >
            <Input
              type="password"
              autoComplete="new-password"
            />
          </FloatLabel>

          <FloatLabel 
            label="Confirm New Password" 
            name="newPasswordConfirm"
            form={form}
            validateTrigger={['onBlur']}
            dependencies={['newPassword']}
            required
            rules={[
              ({ getFieldValue } : any) => ({
                async validator(_: any, value: string) {
                  if (!value || value === getFieldValue('newPassword')) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('Passwords do not match.'));
                },
              }),
            ]}
          >
            <Input
              type="password"
            />
          </FloatLabel>
          <div className="btn-wrapper d-flex justify-content-end">
            <Button type="primary" className='mr-2' loading={props.isCheckingHistory || props.isCheckingPass || props.isUpdatingPass} onClick={submit}>Submit</Button>
          </div>
        </Form>
      </Card>
    </div>
    <div className="mt-3">
      <Table 
        columns={[
          {
            title: 'New Password must meet the following requirements:',
            key: 'requirements',
            dataIndex: 'requirements'
          },
          {
            title: 'Example:',
            key: 'example',
            dataIndex: 'example'
          }
        ]}
        dataSource={[
          {
            key: 1,
            requirements: <>
            1. Cannot be the same as your current password.
            <br />
            2. Cannot contain your user ID.
            <br />
            3. Must be at least 6 characters long.
            <br />
            4. Must contain characters that meet at least 3 criteria:
            <br />
            &nbsp;&nbsp;&nbsp;&nbsp;a. Uppercase characters (A through Z);
                <br />
            &nbsp;&nbsp;&nbsp;&nbsp;b. Lowercase characters (a through z);
                <br />
            &nbsp;&nbsp;&nbsp;&nbsp;c. Number (0 through 9)
                <br />
            &nbsp;&nbsp;&nbsp;&nbsp;d. Special characters ( ~!@#$%^&*(|) ); </>,
            example: <> User ID: Adam123
            <br />
            <br />
            New Password: Adam12301 (Invalid - contains user ID)
            <br />
            New Password: Zadam12301 (Invalid - contains user ID)
            <br />
            New Password: Adam783 (Valid)
            <br />
            New Password: A!dam123 (Valid)
            <br />
            New Password: a!dam123 (Valid) </>,
          },
        ]}
        pagination={false}
      />
    </div>
    </>
  );
};

const mapStateToProps = (store: RootStateOrAny) => ({
  isCheckingPass: store.rootReducer.auth.isCheckingPass,
  isCheckingHistory: store.rootReducer.auth.isCheckingHistory,
  isUpdatingPass: store.rootReducer.auth.isUpdatingPass,
});

const mapDispatchToProps = (dispatch: Dispatch) => bindActionCreators(
  {
    checkCurrentPassword,
    checkPasswordHistory,
    updatePassword,
  },
  dispatch,
);

export default connect(mapStateToProps, mapDispatchToProps)(ChangePasswordComponent);